import { Flex } from '@chakra-ui/react'
import { IoMdBusiness } from 'react-icons/io'
import { useMemo } from 'react'
import { Order_By, useGetOrganizationsQuery } from '../../generated/graphql'
import { genReportFilter } from '../../hooks/report_table_filters'
import { ReportFilterType, OrganizationStatus } from '../../utils/constants'
import { ReportFilterDropdown, SHOW_ALL_FILTER_VALUE } from '../tables/filter_dropdown'
import { IoCubeSharp } from 'react-icons/io5'
import { OrganizationSegment, OrganizationStatusLabels } from '@/generated/enums'
import { MdOutlineAccountTree } from 'react-icons/md'
import { getFilterValues } from '@/hooks/base_filters'
import { useSelectedReportFilters } from '@/pages/reports'

export const ALLOWED_ORG_STATUSES_FOR_UNIFIED_ALERTS = [
  OrganizationStatus.ACTIVE,
  OrganizationStatus.EVALUATING,
  OrganizationStatus.PITCHING,
  OrganizationStatus.TRIAL_ENDED,
]

const getOrgDisplayValue = (value, allOrgsById) => {
  if (value === ReportFilterType.Organization) {
    return 'Org'
  } else {
    return value === SHOW_ALL_FILTER_VALUE
      ? SHOW_ALL_FILTER_VALUE
      : allOrgsById[value].name
  }
}

const getParentOrgDisplayValue = (value, allOrgsById) => {
  if (value === ReportFilterType.ParentOrganization) {
    return 'Parent Org'
  } else {
    return value === SHOW_ALL_FILTER_VALUE
      ? SHOW_ALL_FILTER_VALUE
      : allOrgsById[value].name
  }
}

const getOrgStatusDisplayValue = (value) => {
  if (value === ReportFilterType.OrgStatus) {
    return 'Org Status'
  } else {
    return value === SHOW_ALL_FILTER_VALUE
      ? SHOW_ALL_FILTER_VALUE
      : OrganizationStatusLabels[value]
  }
}

const getOrgSegmentDisplayValue = (value) => {
  if (value === ReportFilterType.OrgSegment) {
    return 'Org Segment'
  } else {
    return value === SHOW_ALL_FILTER_VALUE ? SHOW_ALL_FILTER_VALUE : value
  }
}

const UnifiedReportsTableFilters = () => {
  const [selectedReportFilters, _] = useSelectedReportFilters()
  const { data: allOrgsData, loading: allOrgsLoading } = useGetOrganizationsQuery({
    variables: {
      organizationsWhere: {
        organization_status: {
          _in: ALLOWED_ORG_STATUSES_FOR_UNIFIED_ALERTS,
        },
      },
      orderBy: [{ name: Order_By.Asc }],
    },
  })

  const allOrgsById =
    allOrgsData?.organizations?.reduce((acc, org) => {
      acc[org.id] = org
      return acc
    }, {}) ?? {}

  const allParentOrgsById =
    allOrgsData?.organizations
      ?.filter((org) => org.is_parent)
      .reduce((acc, org) => {
        acc[org.id] = org
        return acc
      }, {}) ?? {}

  const genReportFilters = (reportFilterType: ReportFilterType, values: string[]) => {
    return values.map((value) => genReportFilter(reportFilterType, value))
  }

  const organizationStatusFilterOptions = genReportFilters(
    ReportFilterType.OrgStatus,
    ALLOWED_ORG_STATUSES_FOR_UNIFIED_ALERTS,
  )

  const organizationSegmentFilterOptions = genReportFilters(
    ReportFilterType.OrgSegment,
    Object.values(OrganizationSegment),
  )

  // Determine the list of organization IDs to be used for the filter dropdown
  const organizationIdsForFilter = useMemo(() => {
    if (!allOrgsData?.organizations || !selectedReportFilters) {
      return []
    }
    const filterValues = getFilterValues(
      selectedReportFilters.filter(
        (filter) => filter.filterType === ReportFilterType.ParentOrganization,
      ),
    )
    // Check if specific parents are selected (i.e., not empty and not containing SHOW_ALL_FILTER_VALUE)
    const specificParentsSelected =
      filterValues.length > 0 && !filterValues.includes(SHOW_ALL_FILTER_VALUE)

    if (specificParentsSelected) {
      // If specific parents are selected, filter organizations to include only their children
      return allOrgsData.organizations
        .filter((org) => org.parent?.id && filterValues.includes(org.parent.id))
        .map((org) => org.id)
    } else {
      // Otherwise (no specific parent selected or "Show All" parents selected), show all organizations
      return allOrgsData.organizations.map((org) => org.id)
    }
  }, [allOrgsData?.organizations, selectedReportFilters])

  const organizationFilterOptions = genReportFilters(
    ReportFilterType.Organization,
    organizationIdsForFilter,
  )

  const parentOrganizationFilterOptions = genReportFilters(
    ReportFilterType.ParentOrganization,
    Object.keys(allParentOrgsById),
  )
  if (allOrgsLoading || !allOrgsData?.organizations.length) {
    return <></>
  }

  return (
    <Flex align="left" flexDirection="row" gap={2}>
      <ReportFilterDropdown
        dropdownIcon={<IoCubeSharp size="18" />}
        filterOptions={organizationSegmentFilterOptions}
        filterType={ReportFilterType.OrgSegment}
        isInternal={true}
        isMultiSelect={true}
        valueToDisplayFunction={(value) => getOrgSegmentDisplayValue(value)}
      />

      <ReportFilterDropdown
        dropdownIcon={<IoCubeSharp size="18" />}
        filterOptions={organizationStatusFilterOptions}
        filterType={ReportFilterType.OrgStatus}
        isInternal={true}
        isMultiSelect={true}
        valueToDisplayFunction={(value) => getOrgStatusDisplayValue(value)}
      />

      <ReportFilterDropdown
        dropdownIcon={<IoMdBusiness size="18" />}
        filterOptions={organizationFilterOptions}
        filterType={ReportFilterType.Organization}
        initialValues={[]}
        isInternal={true}
        isMultiSelect={true}
        showSearchBar={true}
        valueToDisplayFunction={(orgId) => getOrgDisplayValue(orgId, allOrgsById)}
      />

      <ReportFilterDropdown
        dropdownIcon={<MdOutlineAccountTree size="18" />}
        filterOptions={parentOrganizationFilterOptions}
        filterType={ReportFilterType.ParentOrganization}
        initialValues={[]}
        isInternal={true}
        isMultiSelect={true}
        showSearchBar={true}
        valueToDisplayFunction={(orgId) =>
          getParentOrgDisplayValue(orgId, allParentOrgsById)
        }
      />
    </Flex>
  )
}

export default UnifiedReportsTableFilters
